// People and compliance plan as a workbook: the items the person can see,
// their key dates and effort, the monthly load for the window on screen, the
// people and the standing commitments. Styled as the call-off export is.
import { downloadXlsx, colName } from '../../utils/xlsxWriter.js';
import { computeLoad, GROUP_LABELS } from '../../utils/peoplePlan.js';

const BORDER = { style: 'thin', color: { rgb: 'FFD1D5DB' } };
const border = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };
const headerStyle = {
  font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: 'FFFFFFFF' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'FF0D1D32' } },
  alignment: { vertical: 'center', wrapText: true },
  border,
};
const bodyStyle = { font: { name: 'Calibri', sz: 11 }, alignment: { vertical: 'top', wrapText: true }, border };
const DATE_FORMAT = 'dd mmmm yyyy';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const toDate = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : ''; };
const monthLabel = (ym) => `${MONTHS[+ym.slice(5, 7) - 1]} ${ym.slice(0, 4)}`;
const round1 = (n) => Math.round(n * 10) / 10;

function sheet(name, headers, rows) {
  const width = (c) => Math.min(60, Math.max(10, String(headers[c]).length + 2, ...rows.map(r => (r[c] instanceof Date ? 14 : String(r[c] ?? '').length + 2))));
  return {
    name,
    rows: [headers.map(v => ({ v, s: headerStyle })),
      ...rows.map(r => headers.map((_, c) => ({ v: r[c] ?? '', s: bodyStyle, z: r[c] instanceof Date ? DATE_FORMAT : undefined })))],
    cols: headers.map((_, c) => ({ wch: width(c) })),
    autofilter: rows.length ? `A1:${colName(headers.length - 1)}${rows.length + 1}` : undefined,
    freeze: { ySplit: 1 },
  };
}

/** The workbook as sheets for utils/xlsxWriter.js. */
export function buildPlanWorkbook({ items, people, standing, months }) {
  const byId = Object.fromEntries(people.map(p => [p.id, p.name]));
  const who = (w) => GROUP_LABELS[w] || byId[w] || w || '';
  const whos = (list) => (list || []).map(who).join(', ');
  const titles = Object.fromEntries(items.map(t => [t.id, t.title]));

  const itemRows = items.map(t => [t.title, t.category, t.type, who(t.owner), whos(t.involved), whos(t.signoff),
    t.start ? monthLabel(t.start) : '', toDate(t.deadline), t.status, t.priority, t.visibility === 'slt' ? 'SLT only' : 'Whole company',
    t.contractors ? 'Yes' : 'No', t.notes, t.process, t.questions, t.assumptions, (t.depends || []).map(d => titles[d]).filter(Boolean).join(', ')]);
  const dates = items.flatMap(t => [
    ...(t.milestones || []).filter(x => x.date).sort((a, b) => a.date.localeCompare(b.date)).map(x => [t.title, toDate(x.date), x.label, x.done ? 'Yes' : 'No']),
    [t.title, toDate(t.deadline), 'Must be done by', t.status === 'Done' ? 'Yes' : 'No'],
  ]);
  const effort = items.flatMap(t => (t.effort || []).map(e => [t.title, t.category, who(e.who), +e.days || 0, e.est ? 'Yes' : 'No']));
  const { L } = computeLoad(items, people, months);
  const load = [...people.map(p => [p.name, ...months.map(m => round1(L[p.id]?.[m] || 0))]),
    ['All employees (days each)', ...months.map(m => round1(L.__all?.[m] || 0))]];

  return [
    sheet('Items', ['Item', 'Category', 'Type', 'Owner', 'Involved', 'Sign-off', 'Work starts', 'Done by', 'Status', 'Priority', 'Visible to',
      'Affects contractors', 'What was said', 'Proposed process (draft)', 'Open questions', 'Assumptions to check', 'Depends on'], itemRows),
    sheet('Key dates', ['Item', 'Date', 'Milestone', 'Done'], dates),
    sheet('Effort', ['Item', 'Category', 'Who', 'Days', 'Estimate'], effort),
    sheet('Monthly load', ['Person', ...months.map(monthLabel)], load),
    sheet('People', ['Name', 'Role', 'Groups', 'Capacity (days a month)', 'Standing (days a month)'],
      people.map(p => [p.name, p.role, (p.groups || []).map(g => GROUP_LABELS[g]).join(', '), p.capacity ?? '', p.standing_days ?? ''])),
    sheet('Standing commitments', ['Name', 'Cadence', 'Owner', 'Attendees', 'Status', 'Notes'],
      standing.map(s => [s.title, s.cadence, who(s.owner), s.attendees, s.status, s.notes])),
  ];
}

export function downloadPlanExport(data) {
  downloadXlsx(buildPlanWorkbook(data), 'AV_people_and_compliance_plan.xlsx');
}
